import {
	faArrowsMaximize,
	faComputerMouseScrollwheel,
	faMagnifyingGlassMinus,
	faMagnifyingGlassPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef, useState } from 'react';
import ReactFlow, { Edge, Node, Position, ReactFlowInstance } from 'react-flow-renderer';

import useTranslation from 'hooks/useTranslation';
import { Appearance } from 'types/ShoppingCart/Appearance';
import { Product, renderWeight } from 'types/ShoppingCart/Product';
import { ShoppingCart } from 'types/ShoppingCart/ShoppingCart';

interface IProps {
	product: Product.Type;
	isOpen: boolean;
	cart: ShoppingCart.Type;
	onHide: () => void;
}

const nodeTypeMap = {
	Recipe: { x: 0, backgroundColor: '#d4ffd4' },
	Menu: { x: 200, backgroundColor: '#ffecd1' },
	MenuPlan: { x: 400, backgroundColor: '#E0FFFF' },
};

function sortSourcesByType(sources: Appearance.Type[]): Appearance.Type[] {
	const typeOrder = {
		Recipe: 1,
		Menu: 2,
		MenuPlan: 3,
	};

	return sources.sort((a, b) => {
		if (a.type === 'UserChange' || b.type === 'UserChange') return 1;
		return typeOrder[a.type] - typeOrder[b.type];
	});
}

function getProductQuantityFromSource(cart: any, sourceId: string, productId: string): number {
	const products: Product.Type[] = ShoppingCart.getProductsFromAppearanceId(cart, sourceId);

	const product = products.filter((product) => product.id === productId);

	return product ? calculateTotalWeightForId(product, productId) : 0;
}
function calculateTotalWeightForId(products: Product.Type[], targetId: string): number {
	return products
		.filter((product) => product.id === targetId)
		.reduce((total, product) => total + product.weight * product.quantity, 0);
}

function generateNodesAndEdges(props: IProps) {
	const nodes: Node[] = [];
	const edges: Edge[] = [];

	const { t, tl, cc } = useTranslation();

	const sources = sortSourcesByType(ShoppingCart.searchSources(props.cart, '', cc));

	const yStart = 450;
	const ySpacing = 100;
	const xTotal = 780;
	const xProduct = 600;
	const LEGEND_Y_POSITION = 600;
	let yOffset = 0;
	let totalProductWeight = 0;

	sources.forEach((source) => {
		if (source.type === 'UserChange') return;
		const nodeType = nodeTypeMap[source.type];
		if (!nodeType) return;
		if (getProductQuantityFromSource(props.cart, source.id, props.product.ingredient.id) === 0)
			return;

		const sourceNodeId = `source-${tl(source.name)}`;

		nodes.push({
			id: sourceNodeId,
			data: { label: tl(source.name) },
			position: { x: nodeType.x, y: yStart + yOffset },
			targetPosition: Position.Left,
			sourcePosition: Position.Right,
			style: {
				backgroundColor: nodeType.backgroundColor,
				fontWeight: 'bold',
			},
		});

		const weightNodeId = `weight-${tl(source.name)}`;
		nodes.push({
			id: weightNodeId,
			data: {
				label: `${renderWeight(
					getProductQuantityFromSource(props.cart, source.id, props.product.id),
					props.product,
					true
				)}`,
			},
			position: { x: -100, y: yStart + yOffset },
			targetPosition: Position.Left,
			sourcePosition: Position.Right,
			style: {
				backgroundColor: '#6699CC',
				fontWeight: 'bold',
				borderRadius: '1rem',
				width: '4rem',
				color: 'white',
			},
		});

		edges.push({
			id: `e-${weightNodeId}-${sourceNodeId}`,
			source: weightNodeId,
			target: sourceNodeId,
			animated: true,
		});

		totalProductWeight +=
			getProductQuantityFromSource(props.cart, source.id, props.product.id) || 0;
		yOffset -= ySpacing;
	});

	const productNodeId = 'product-name';
	nodes.push({
		id: productNodeId,
		data: { label: tl(props.product.ingredient.name) },
		position: { x: xProduct, y: yStart },
		targetPosition: Position.Left,
		sourcePosition: Position.Right,
		style: {
			backgroundColor: '#d1e1ff',
			fontWeight: 'bold',
		},
	});

	const totalNodeId = 'total-weight';
	nodes.push({
		id: totalNodeId,
		data: { label: `${Product.renderWeight(props.product.weight, props.product, true)}` },
		position: { x: xTotal, y: yStart },
		targetPosition: Position.Left,
		sourcePosition: Position.Right,
		style: {
			backgroundColor: '#6699CC',
			fontWeight: 'bold',
			borderRadius: '1rem',
			color: 'white',
			width: '4rem',
		},
	});

	sources.forEach((source) => {
		const sourceNodeId = `source-${tl(source.name)}`;
		edges.push({
			id: `e-${sourceNodeId}-${productNodeId}`,
			source: sourceNodeId,
			target: productNodeId,
			animated: true,
		});
	});

	edges.push({
		id: `e-${productNodeId}-${totalNodeId}`,
		source: productNodeId,
		target: totalNodeId,
		animated: true,
	});

	const legendNodes: Node[] = [
		{
			id: 'weight-legend',
			data: { label: t('shoppingCart:WEIGHT') },
			position: { x: -100, y: LEGEND_Y_POSITION },
			sourcePosition: Position.Right,
			targetPosition: Position.Right,
			style: {
				backgroundColor: '#6699CC',
				fontWeight: 'bold',
				borderRadius: '1rem',
				width: '4rem',
				color: 'white',
				opacity: 0.7,
			},
		},
		{
			id: 'recipe-legend',
			data: { label: 'Recipes' },
			position: { x: 0, y: LEGEND_Y_POSITION },
			sourcePosition: Position.Right,
			targetPosition: Position.Left,
			style: {
				backgroundColor: '#d4ffd4',
				fontWeight: 'bold',
				opacity: 0.7,
			},
		},
		{
			id: 'menu-legend',
			data: { label: 'Menus' },
			position: { x: 200, y: LEGEND_Y_POSITION },
			targetPosition: Position.Left,
			sourcePosition: Position.Right,
			style: {
				backgroundColor: '#ffecd1',
				fontWeight: 'bold',
				opacity: 0.7,
			},
		},
		{
			id: 'menuplan-legend',
			data: { label: 'MenuPlans' },
			position: { x: 400, y: LEGEND_Y_POSITION },
			targetPosition: Position.Left,
			sourcePosition: Position.Right,
			style: {
				backgroundColor: '#E0FFFF',
				fontWeight: 'bold',
				opacity: 0.7,
			},
		},
		{
			id: 'ingredient-legend',
			data: { label: t('shoppingCart:PRODUCTS') },
			position: { x: 600, y: LEGEND_Y_POSITION },
			targetPosition: Position.Left,
			sourcePosition: Position.Right,
			style: {
				backgroundColor: '#d1e1ff',
				fontWeight: 'bold',
				opacity: 0.7,
			},
		},
		{
			id: 'total-legend',
			data: { label: t('_general:TOTAL') },
			position: { x: 780, y: LEGEND_Y_POSITION },
			targetPosition: Position.Left,
			sourcePosition: Position.Right,
			style: {
				backgroundColor: '#6699CC',
				fontWeight: 'bold',
				borderRadius: '1rem',
				color: 'white',
				width: '4rem',
				opacity: 0.7,
			},
		},
	];

	edges.push(
		{
			id: `e-weight-legend-recipe-legend`,
			source: 'weight-legend',
			target: 'recipe-legend',
			animated: true,
		},
		{
			id: `e-recipe-legend-menu-legend`,
			source: 'recipe-legend',
			target: 'menu-legend',
			animated: true,
		},
		{
			id: `e-menu-legend-menuplan-legend`,
			source: 'menu-legend',
			target: 'menuplan-legend',
			animated: true,
		},
		{
			id: `e-menuplan-legend-product-legend`,
			source: 'menuplan-legend',
			target: 'ingredient-legend',
			animated: true,
		},
		{
			id: `e-product-legend-total-legend`,
			source: 'ingredient-legend',
			target: 'total-legend',
			animated: true,
		}
	);

	return { nodes: nodes.concat(legendNodes), edges };
}

const SourcesDiagram = (props: IProps) => {
	const { nodes, edges } = generateNodesAndEdges(props);
	const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);

	const [isFitted, setIsFitted] = useState(true);
	const [firstMove, setFirstMove] = useState(true);

	const updateIsFitted = (instance: ReactFlowInstance) => {
		if (instance && isFitted && !firstMove) {
			const currentTransform = instance.toObject().viewport;
			const { x, y, zoom } = currentTransform;
			setIsFitted(Math.abs(x) < 1 && Math.abs(y) < 1 && Math.abs(zoom - 1) < 0.01);
		}
		setFirstMove(false);
	};

	const handleFitView = () => {
		if (reactFlowInstanceRef.current) {
			reactFlowInstanceRef.current.fitView();
			setIsFitted(true);
		}
	};

	const Diagram = () => {
		const renderZoomControls = () => (
			<div className="d-flex align-items-center" style={{ color: '#6699cc' }}>
				{[faMagnifyingGlassPlus, faComputerMouseScrollwheel, faMagnifyingGlassMinus].map(
					(icon, index) => (
						<FontAwesomeIcon
							key={index}
							icon={icon}
							style={{
								marginRight: index === 2 ? '1rem' : '0.5rem',
								opacity: index === 1 ? '1' : '0.8',
								fontSize: index === 1 ? '1.5rem' : 'inherit',
							}}
						/>
					)
				)}
			</div>
		);

		const renderFitViewButton = () =>
			!isFitted && (
				<button className="btn btn-outline-primary btn-sm" onClick={handleFitView}>
					<FontAwesomeIcon icon={faArrowsMaximize} />
				</button>
			);

		return (
			<>
				<div className="d-flex justify-content-end mb-2 me-2" style={{ height: '2rem' }}>
					{renderZoomControls()}
					{renderFitViewButton()}
				</div>
				<div style={{ height: 500 }}>
					<ReactFlow
						nodes={nodes}
						edges={edges}
						fitView
						nodesDraggable={false}
						onInit={(instance) => {
							reactFlowInstanceRef.current = instance;
							handleFitView();
						}}
						onMove={() => updateIsFitted(reactFlowInstanceRef.current!)}
					/>
				</div>
			</>
		);
	};

	return Diagram();
};

export default SourcesDiagram;
